import * as React from 'react';
import { useEffect, useRef } from 'react';
import useResizeObserver from '@react-hook/resize-observer';

import { useStores } from 'hooks';

import { getInstanceFromUrl, sendResizeMessage } from 'modules/resize-message';

import {
  getBrandingConfig,
  getRequestedLobsConfig,
  getSearchConfig,
  Panel,
  resolveDestinationParam,
  useSearchWidgetContext,
} from 'features/search-widget';

function SearchWidget(): JSX.Element {
  const {
    app: { program, routeQueries },
  } = useStores();

  const { setBranding, setLobs, setSearch, selectedLob, setToDestination } = useSearchWidgetContext();

  const widget = useRef<HTMLElement>();
  const destinationPrefillApplied = useRef(false);

  useEffect(() => {
    setBranding(getBrandingConfig({ programId: program.id }));
    setLobs(getRequestedLobsConfig({ programId: program.id, lobIds: routeQueries['lobs'] }));
    setSearch(getSearchConfig({ programId: program.id }));
  }, [program, routeQueries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Curated/passthrough region IDs are resolved against hotel-style geography, so
    // the prefill only applies to the stays LOB (not flights). selectedLob isn't
    // available until the effect above has run setLobs, so this stays a separate effect.
    //
    // Apply at most once: this effect re-runs any time selectedLob's reference changes
    // (e.g. switching to the flights tab and back to stays), and re-applying on every
    // such transition would silently clobber a destination the user has since cleared
    // or changed via the typeahead.
    if (destinationPrefillApplied.current || selectedLob?.id !== 'stays') {
      return;
    }

    destinationPrefillApplied.current = true;

    const prefill = resolveDestinationParam(routeQueries['destination']);

    if (prefill) {
      setToDestination(prefill);
    }
  }, [selectedLob, routeQueries]); // eslint-disable-line react-hooks/exhaustive-deps

  useResizeObserver(widget, function handleResize(entry): void {
    sendResizeMessage({
      type: 'eg-widget/resize',
      meta: {
        // Read instance from the live iframe URL, not the hydrated store, so the
        // SSR response stays cacheable across loads (see getInstanceFromUrl).
        instance: getInstanceFromUrl(),
      },
      payload: {
        frame: {
          style: {
            width: '100%',
            height: `${entry.contentRect.height}px`,
          },
        },
      },
    });
  });

  return (
    <article ref={widget} className="widget">
      <Panel />
    </article>
  );
}

export { SearchWidget };
